/**
 * Client mount harness. Every site's main.tsx ended with the same dance:
 * server-rendered markup (root has children) → hydrate; empty root (dev / a
 * standalone build) → fresh render. Fonts and globals.css stay in the site's
 * main.tsx (they are per-design); the site imports them, then calls mountClient.
 *
 *   import './globals.css'
 *   import App from './App'
 *   import { mountClient } from '@gjl/site-kit'
 *   mountClient(App)
 */
import { StrictMode, type ComponentType } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { initGA4 } from '@gjl/analytics'
import { PRERENDERED_PATH_ATTR, hydrationDecision, localeBasename } from '@gjl/types'
import type { InjectedCmsData } from './cms/types'
import { PlatformShell, type PlatformOptions } from './platform-routes'
import { SiteFrame } from './site-frame'
import { LocaleProvider } from './locale/LocaleProvider'

export interface MountOptions {
  rootId?: string
  /** Opt into platform routes (client galleries, …) injected ahead of the site. */
  platform?: PlatformOptions
  /**
   * Set false to skip the publish-configured GA4 init (see `mountAnalytics`).
   * For a site that wires its own analytics — e.g. behind `mountConsentedAnalytics`
   * — so the two do not both call `gtag('config', …)`.
   */
  analytics?: boolean
  /**
   * GA4 measurement id for a site that is NOT CMS-published, and so has no
   * `window.__CMS_DATA__` to read one from. First-party builds (fuguelab.com's
   * marketing client) pass it here rather than hand-rolling an inline gtag
   * snippet — that snippet is what left the studio site reporting one page_view
   * per hard load and none for client-side navigation, because `initGA4` never
   * ran and the `<PageviewTracker/>` below silently no-opped for months.
   *
   * The injected CMS value still wins when present, so a published client site
   * keeps behaving exactly as before.
   */
  ga4MeasurementId?: string
}

/**
 * Initialise GA4 from the measurement id the publish baked into the bundle.
 *
 * WHY THIS LIVES IN THE MOUNT HARNESS, like ScrollReset above it: it is not a
 * design decision any site should be able to forget, and 35 of 36 client sites
 * did forget it. `ga4MeasurementId` has been editable in the app (Website →
 * Settings → Google Analytics 4) and injected into `window.__CMS_DATA__` on every
 * publish for months, while exactly ONE site — demo-great-host-composers — read
 * it. Everywhere else a client could paste their measurement id, publish, and
 * collect nothing, with no error and no way to tell.
 *
 * NOT consent-gated, preserving the owner decision recorded in that site's
 * main.tsx on 2026-07-27: these sites track every visitor with no cookie banner.
 * That decision is now in one place instead of one site. Consent-gating without
 * also rendering `<CookieConsent>` would be worse than the bug being fixed —
 * analytics would initialise never rather than always, and look wired either way.
 * A site that wants the gate passes `analytics: false` and calls
 * `mountConsentedAnalytics` itself.
 *
 * The id is baked at PUBLISH time: saving the setting changes nothing until the
 * next publish. `initGA4` no-ops on a falsy id, so local dev and any org that has
 * not set one stay untracked.
 */
function mountAnalytics(fallbackMeasurementId?: string): void {
  if (typeof window === 'undefined') return
  const injected = (window as { __CMS_DATA__?: InjectedCmsData }).__CMS_DATA__
  initGA4(injected?.siteConfig?.ga4MeasurementId ?? fallbackMeasurementId)
}

/**
 * Mount a site's App. `opts` is backward-compatible: a bare string is the legacy
 * rootId; an object is { rootId?, platform? }. Passing `platform` injects the
 * shared platform route table (e.g. /g/:token) so the site needn't hardcode it.
 */
export function mountClient(App: ComponentType, opts: string | MountOptions = {}): void {
  const {
    rootId = 'root',
    platform,
    analytics = true,
    ga4MeasurementId,
  } = typeof opts === 'string'
    ? { rootId: opts, platform: undefined, analytics: true, ga4MeasurementId: undefined }
    : opts
  const el = document.getElementById(rootId)
  if (!el) throw new Error(`mountClient: #${rootId} element not found`)

  if (analytics) mountAnalytics(ga4MeasurementId)

  const appEl = platform ? <PlatformShell appElement={<App />} platform={platform} /> : <App />
  // ScrollReset and PageviewTracker live here rather than in each site's App:
  // both need router context (so neither can wrap BrowserRouter) and neither is
  // a design decision any site should be able to forget. PageviewTracker is
  // rendered LAST on purpose — see pageview-tracker.tsx for why the order is
  // load-bearing. See scroll-reset.tsx.
  // The language tree this shell belongs to — the same payload field
  // `createRenderBody` reads, so both sides agree on basename and locale.
  const locale = (window as { __CMS_DATA__?: InjectedCmsData }).__CMS_DATA__?.locale
  const tree = (
    <StrictMode>
      <BrowserRouter basename={localeBasename(locale) || undefined}>
        <LocaleProvider locale={locale}>
          {/* The SAME frame `createRenderBody` renders, or every `useId` differs. See site-frame.tsx. */}
          <SiteFrame>{appEl}</SiteFrame>
        </LocaleProvider>
      </BrowserRouter>
    </StrictMode>
  )

  // Hydrate ONLY when this shell is THIS route. A route that was never
  // prerendered (a param/detail route, /apply) is served the HOME shell by
  // CloudFront's SPA fallback — populated #root, wrong markup — and hydrating
  // that is a guaranteed mismatch that silently throws the SSR away. An
  // UNSTAMPED shell keeps the old behaviour, so nothing already published
  // changes. See @gjl/types/prerender-stamp.ts for the three states and the
  // live measurement that motivated them.
  const decision = hydrationDecision(
    el.getAttribute(PRERENDERED_PATH_ATTR),
    window.location.pathname,
    el.hasChildNodes()
  )
  if (decision === 'hydrate') {
    hydrateRoot(el, tree)
  } else {
    createRoot(el).render(tree)
  }
}
